/**
 * useHaptics - Hook for haptic feedback using Capacitor Haptics
 * Provides simple methods for common haptic patterns
 */
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

// Check if we're running in a native context
const isNative = typeof window !== 'undefined' && 'Capacitor' in window;

export function useHaptics() {
  /**
   * Light impact - for button taps, selections
   */
  const lightTap = async () => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (e) {
      // Haptics not available - fail silently
    }
  };

  /**
   * Medium impact - for confirmations, selections
   */
  const mediumTap = async () => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (e) {
      // Haptics not available - fail silently
    }
  };

  /**
   * Heavy impact - for significant actions
   */
  const heavyTap = async () => {
    if (!isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (e) {
      // Haptics not available - fail silently
    }
  };

  /**
   * Success notification - for completed actions
   */
  const success = async () => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (e) {
      // Haptics not available - fail silently
    }
  };

  /**
   * Warning notification - for alerts
   */
  const warning = async () => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (e) {
      // Haptics not available - fail silently
    }
  };

  /**
   * Error notification - for errors
   */
  const error = async () => {
    if (!isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (e) {
      // Haptics not available - fail silently
    }
  };

  /**
   * Selection changed - for picker/scroll selections
   */
  const selectionChanged = async () => {
    if (!isNative) return;
    try {
      await Haptics.selectionChanged();
    } catch (e) {
      // Haptics not available - fail silently
    }
  };

  return {
    lightTap,
    mediumTap,
    heavyTap,
    success,
    warning,
    error,
    selectionChanged,
  };
}

export default useHaptics;
